import { useState } from "react";
import { toast } from "react-toastify";
import { Button, Card, CardContent, CardFooter } from "@pushpress/pushpress-ui";
import { StripePaymentElementOptions } from "@stripe/stripe-js";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useMutation } from "@tanstack/react-query";
import { postSavePaymentMethod } from "../../../../../../apis/billingApi";
import { queryClient } from "../../../../../../components/providers/ReactQueryProvider";
import {
  extractError,
  defaultToastOptions,
} from "../../../../../../utils/misc";
import SuccessScreen from "./SuccessScreen";
import type { RequestType } from "../Billing";

const basePaymentElementOptions: StripePaymentElementOptions = {
  wallets: {
    applePay: "never",
    googlePay: "never",
  },
};

type PaymentComponentProps = {
  userUuid: string;
  clientUuid: string;
  stripeCustomer: string;
  closeAddPaymentModal: () => void;
  requestType?: RequestType;
};

export default function PaymentComponent({
  userUuid,
  clientUuid,
  stripeCustomer,
  closeAddPaymentModal,
  requestType,
}: PaymentComponentProps) {
  const stripe = useStripe();
  const elements = useElements();

  const [isElementLoading, setIsElementLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);

  const { mutateAsync: savePaymentMethod } = useMutation(postSavePaymentMethod);

  const handleSavePaymentMethod = async () => {
    setIsSaving(true);
    try {
      if (!elements || !stripe) {
        throw new Error("Stripe not found");
      }
      const { setupIntent, error } = await stripe.confirmSetup({
        elements: elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        throw new Error(extractError(error));
      }
      if (!setupIntent) {
        throw new Error("There was an error saving your payment method");
      }

      const result = await savePaymentMethod({
        userUuid,
        clientUuid,
        paymentMethodId: setupIntent.payment_method as string,
        customerId: stripeCustomer,
      });

      if (!result.wasSuccessful || !result.billing) {
        throw new Error("There was an error adding your payment method");
      } else {
        // Invalidate the user-payment-methods query to refetch billing data
        queryClient.invalidateQueries({ queryKey: ["user-payment-methods"] });
        setShowSuccessScreen(true);
      }
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    } finally {
      setIsSaving(false);
    }
  };

  if (showSuccessScreen) {
    return <SuccessScreen onClose={() => closeAddPaymentModal()} />;
  }

  return (
    <div>
      <Card className="tw-w-full tw-max-w-lg tw-mx-auto tw-max-h-[80vh] tw-overflow-hidden">
        <CardContent className="tw-w-full tw-mt-6 tw-overflow-y-auto tw-max-h-[calc(80vh-100px)]">
          <PaymentElement
            options={{
              ...basePaymentElementOptions,
              paymentMethodOrder:
                requestType === "bank" ? ["us_bank_account"] : ["card"],
            }}
            onReady={() => setIsElementLoading(false)}
            onLoaderStart={() => setIsElementLoading(true)}
          />
          {!isElementLoading && (
            <CardFooter className="tw-mt-6 tw-w-full tw-px-0 tw-py-0">
              <Button
                className="tw-w-full"
                onClick={handleSavePaymentMethod}
                disabled={!stripe}
                isSubmitting={isSaving}
              >
                Save
              </Button>
            </CardFooter>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
